import { keepPreviousData as keepPreviousDataPlaceholder, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useApplicationContext } from '../../modules/context/ApplicationContext/ApplicationContext';
import { useNetworkContext } from '../../modules/offline/contexts/network/network.context';
import {
  UseDataQuery,
  UseDataQueryHandleRefetchParams,
  UseDataQueryOptions,
} from './dataQuery.types';

const useDataQuery = <T>(
  {
    teamId,
    queryKey,
    queryFn,
    staleTime,
    gcTime,
    refetchOnMount = true,
    keepPreviousData = false,
  }: UseDataQuery<T>,
  options: UseDataQueryOptions<T> = {},
) => {
  const { team } = useApplicationContext();
  const { isOnline } = useNetworkContext();

  // TODO: HOJE TEMOS O team.id SENDO PEGO POR UM HOOK PARA DIFERENCIAR O TIME DO USER, MAS NA FORMA QUE CRIAMOS NOSSAS KEYS DENTRO DE NOSSOS SERVIÇOS NÃO ESTAMOS PASSANDO O team.id O QUE DIFICULTA NA HORA DE REUTILIZAR A FUNÇÃO DE RECRIAR A KEY EM UM OUTRO CONTEXTO QUE NÃO USANDO COM ESTE HOOK, IMAGINO QUE O TEAM.ID DEVERIA SER PASSADO COMO PARAMETRO PARA A FUNÇÃO QUE CRIA A KEY, OU A FUNÇÃO RETORNAR O TEAM.ID PARA QUE POSSAMOS USAR EM OUTRO LUGAR
  const queryKeyWithTeamId = [team?.id ?? teamId, ...queryKey];

  const { params: _ignoredParams, ...restOptions } = options;

  const response = useQuery<T, unknown, T>({
    queryFn,
    staleTime,
    refetchOnWindowFocus: false,
    refetchOnMount,
    gcTime,
    queryKey: queryKeyWithTeamId,
    ...(keepPreviousData ? { placeholderData: keepPreviousDataPlaceholder } : {}),
    ...restOptions,
  });

  const { refetch } = response;

  const handleRefetch = useCallback(
    ({ ignoreOnlineValidation }: UseDataQueryHandleRefetchParams = {}) => {
      if (!isOnline && !ignoreOnlineValidation) {
        return;
      }
      return refetch();
    },
    [refetch, isOnline],
  );

  return {
    queryFn,
    queryKey: queryKeyWithTeamId,
    data: response.data,
    isLoading: response.isPending && response.fetchStatus !== 'idle', // https://github.com/TanStack/query/issues/3975
    isError: response.isError,
    error: response.error,
    refetch: handleRefetch,
    isFetched: response.isFetched,
    isFetchedAfterMount: response.isFetchedAfterMount,
    isSuccess: response.isSuccess,
    isFetching: response.isFetching,
  };
};

export default useDataQuery;
